import { Link } from "react-router-dom";
import { CustomerLink } from "@/components/EntityLink";
import { SearchBox, useUrlSearch } from "@/components/SearchBox";
import { listCustomers, type AdminCustomerRow } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";
import { day, money, when } from "@/lib/format";
import { Notice } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { PagedList, usePagedCursor } from "@/components/ui/paged-list";
import { TD, TH, TR } from "@/components/ui/table";

/**
 * Everyone who has bought something, assembled from the only thing that
 * identifies a person here.
 *
 * THERE IS NO CUSTOMER TABLE. This shop sells having no account as the product
 * -- "No ID, no account" -- so nothing in the database means "a person". What
 * there is, is `orders.email`, and this list is that column grouped.
 *
 * TWO CONSEQUENCES THE SCREEN HAS TO STATE, because a reader who assumes
 * otherwise would be wrong and the screen would look right.
 *
 *   * AN ORDER WITH NO EMAIL IS IN NO CUSTOMER. Those orders are real and this
 *     list cannot show them. An operator who took this for the whole ledger
 *     would believe they had seen every order, so the notice says where the
 *     rest are.
 *
 *   * "SPENT" IS PAID ORDERS ONLY. An abandoned checkout also has a total, and
 *     counting it would turn an unpaid invoice into revenue.
 */
export function CustomersScreen() {
  const search = useUrlSearch();
  const q = search.value;

  const pager = usePagedCursor(`customers:${q}`);
  const { data, loading, error } = useAsync(
    () => listCustomers({ cursor: pager.cursor, q }),
    [pager.cursor, q],
  );

  const rows = data?.rows ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Customers"
        description="Orders grouped by the address they were bought with."
      >
        <SearchBox
          search={search}
          placeholder="Any part of an address"
          label="Find a customer by any part of their address"
        />
      </PageHeader>

      <Notice tone="info">
        This shop has no accounts, so a customer is an email address. Orders placed without one are
        on the <Link to="/orders" className="font-medium underline underline-offset-4">Orders</Link>{" "}
        screen and cannot appear here.
      </Notice>

      <PagedList
        head={
          <>
            <TH>Address</TH>
            <TH numeric>Orders</TH>
            <TH numeric>Paid</TH>
            <TH numeric>Spent</TH>
            <TH>First order</TH>
            <TH>Last order</TH>
          </>
        }
        rows={rows}
        columns={6}
        loading={loading}
        error={error}
        empty={q ? "No address contains that." : "Nobody has ordered with an email address yet."}
        pager={pager}
        nextCursor={data?.nextCursor}
        renderRow={(row: AdminCustomerRow) => (
          <TR key={row.id}>
            <TD>
              <CustomerLink email={row.email} />
            </TD>
            <TD numeric>{row.orders}</TD>
            {/* Paid is a SECOND number rather than the only one: the gap
                between them is abandoned checkouts, which is a thing an
                operator wants to see and a single "orders" column hides. */}
            <TD numeric className="text-secondary">{row.paid_orders}</TD>
            <TD numeric className="font-medium">{money(row.spent_cents)}</TD>
            <TD className="whitespace-nowrap text-muted-foreground">{day(row.first_order_at)}</TD>
            <TD className="whitespace-nowrap text-muted-foreground">{when(row.created_at)}</TD>
          </TR>
        )}
      />
    </div>
  );
}
